import { SparkDemandMetricsByTime } from '@pubstack/common/src/analytics/query'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { Timezone } from '@pubstack/common/src/timezone'
import { DateTime } from 'luxon'
import { FunctionComponent } from 'react'
import { Colors } from '~/assets/style/colors'
import { WidgetProps } from '~/components/Widget'
import { AnalyticsDefaultChartOptions } from '~/modules/analytics/AnalyticsCharts'
import { AnalyticsChartWidget } from '~/modules/analytics/AnalyticsChartWidget'
import { MetricConfiguration, TimelineConfiguration, useMetrics, useTimelineChart } from '~/modules/analytics/analyticsTimeline.hooks'
import { sparkDemandEstimatedEcpm, sparkDemandEstimatedImpressions, sparkDemandEstimatedRevenue } from '~/modules/analytics/common/formulas'
import { WithClassName } from '~/types/utils'
import { displayWithCurrency } from '~/utils/string'

type Config = MetricConfiguration<SparkDemandMetricsByTime> & TimelineConfiguration<SparkDemandMetricsByTime>

const monetaryChartOptions: Config['getChartOptions'] = ({ currencySymbol }) => ({
  seriesType: 'bars',
  vAxis: { ...AnalyticsDefaultChartOptions.vAxis, format: displayWithCurrency('#.##', currencySymbol) },
})

const CONFIG: Config[] = [
  {
    getChartOptions: monetaryChartOptions,
    metric: { name: 'revenue', tooltipText: 'Spark demand revenue with the billing adjustment applied.' },
    legendConfig: ['revenue'],
    tooltipConfig: ['revenue'],
    dataConfig: [{ name: 'revenue', label: sparkDemandEstimatedRevenue.name, iconColor: Colors.SteelBlue, getFormula: () => sparkDemandEstimatedRevenue }],
  },
  {
    getChartOptions: () => ({ seriesType: 'bars' }),
    metric: { name: 'impressionCount', tooltipText: 'Spark demand impressions with the billing adjustment applied.' },
    legendConfig: ['impressionCount'],
    tooltipConfig: ['impressionCount'],
    dataConfig: [{ name: 'impressionCount', label: sparkDemandEstimatedImpressions.name, iconColor: Colors.SteelBlue, getFormula: () => sparkDemandEstimatedImpressions }],
  },
  {
    getChartOptions: monetaryChartOptions,
    metric: { name: 'ecpm', tooltipText: 'Estimated revenue / Estimated impressions * 1000' },
    legendConfig: ['ecpm'],
    tooltipConfig: ['ecpm'],
    dataConfig: [{ name: 'ecpm', label: sparkDemandEstimatedEcpm.name, iconColor: Colors.SteelBlue, getFormula: () => sparkDemandEstimatedEcpm }],
  },
]

type SparkDemandTimelineProps = WithClassName &
  Omit<WidgetProps, 'title' | 'info' | 'icon' | 'refreshTrackingLabel'> & {
    data: SparkDemandMetricsByTime
    currencySymbol: CurrencySymbol
    currentEpoch: DateTime
    timezone: Timezone
  }

export const SparkDemandTimeline: FunctionComponent<SparkDemandTimelineProps> = ({ data, currencySymbol, currentEpoch, timezone, ...props }) => {
  const { metrics, currentConfig } = useMetrics({ config: CONFIG, data, currencySymbol, onMetricChange: () => {} })
  const { chart, legends } = useTimelineChart({ currentConfig, data, currencySymbol, currentEpoch, timezone })

  return <AnalyticsChartWidget {...props} icon={'chart_bar'} title={'Performance'} metrics={metrics} chart={chart} legends={legends} refreshTrackingLabel={'ana-spark-demand-timeline-refresh'} />
}
